/**
 * What the payload cache remembers about a document besides its bytes. Kept in a store of its own so the
 * least-recently-used scan never deserializes payloads.
 */
export interface HsHlodPayloadMeta {
  bytes: number;
  /** Last read or write, from the cache's clock; eviction order. */
  touchedAt: number;
  /** The `HSHL` payload version, or null for manifests and subtrees (JSON). */
  version: number | null;
  /** When the document was last downloaded; version-1 payloads are checked for a KTX2 replacement after a while. */
  validatedAt: number;
}

export interface HsHlodStoredPayload {
  payload: ArrayBuffer;
  meta: HsHlodPayloadMeta;
}

export interface HsHlodPayloadStoreItem extends HsHlodStoredPayload {
  id: string;
}

/** Where the payload cache keeps documents; the IndexedDB store persists them, the memory store does not. */
export interface HsHlodPayloadStore {
  /** The stored documents among `ids`; missing ids are absent from the result. */
  read(ids: readonly string[]): Promise<Map<string, HsHlodStoredPayload>>;
  /** Stores or replaces documents. */
  write(items: readonly HsHlodPayloadStoreItem[]): Promise<void>;
  /** Moves documents to the end of the eviction order. */
  touch(ids: readonly string[], touchedAt: number): Promise<void>;
  /** Every stored document's meta, least recently touched first. */
  entries(): Promise<{ id: string; meta: HsHlodPayloadMeta }[]>;
  delete(ids: readonly string[]): Promise<void>;
  clear(): Promise<void>;
}

/** Keeps payloads in memory for the lifetime of the tab: for tests, and for hosts that must not persist documents. */
export class HsHlodMemoryPayloadStore implements HsHlodPayloadStore {
  private readonly items: Map<string, HsHlodStoredPayload> = new Map();

  public async read(ids: readonly string[]): Promise<Map<string, HsHlodStoredPayload>> {
    const result: Map<string, HsHlodStoredPayload> = new Map();
    ids.forEach((id: string) => {
      const item: HsHlodStoredPayload | undefined = this.items.get(id);
      if (item !== undefined) {
        result.set(id, { payload: item.payload, meta: { ...item.meta } });
      }
    });
    return result;
  }

  public async write(items: readonly HsHlodPayloadStoreItem[]): Promise<void> {
    items.forEach((item: HsHlodPayloadStoreItem) =>
      this.items.set(item.id, { payload: item.payload, meta: { ...item.meta } })
    );
  }

  public async touch(ids: readonly string[], touchedAt: number): Promise<void> {
    ids.forEach((id: string) => {
      const item: HsHlodStoredPayload | undefined = this.items.get(id);
      if (item !== undefined) {
        item.meta.touchedAt = touchedAt;
      }
    });
  }

  public async entries(): Promise<{ id: string; meta: HsHlodPayloadMeta }[]> {
    return Array.from(this.items, ([id, item]: [string, HsHlodStoredPayload]) => ({ id, meta: { ...item.meta } })).sort(
      (left: { meta: HsHlodPayloadMeta }, right: { meta: HsHlodPayloadMeta }) =>
        left.meta.touchedAt - right.meta.touchedAt
    );
  }

  public async delete(ids: readonly string[]): Promise<void> {
    ids.forEach((id: string) => this.items.delete(id));
  }

  public async clear(): Promise<void> {
    this.items.clear();
  }
}

export interface HsHlodIndexedDbPayloadStoreOptions {
  /** Default `hyperscene-hlod`; hosts that embed several viewers may share one database. */
  databaseName?: string;
  indexedDb?: IDBFactory;
}

const PAYLOADS_STORE: string = 'payloads';
const META_STORE: string = 'meta';
const TOUCHED_INDEX: string = 'touchedAt';
const SCHEMA_VERSION: number = 1;

/**
 * Persists payloads in IndexedDB: one object store with the bytes and one with the small meta records, indexed by
 * `touchedAt`, so eviction scans only the meta store. Every method rejects when IndexedDB is unavailable or the
 * database cannot be opened; the cache turns itself off on that.
 */
export class HsHlodIndexedDbPayloadStore implements HsHlodPayloadStore {
  private database: Promise<IDBDatabase> | null = null;

  public constructor(private readonly options: HsHlodIndexedDbPayloadStoreOptions = {}) {}

  public async read(ids: readonly string[]): Promise<Map<string, HsHlodStoredPayload>> {
    const db: IDBDatabase = await this.open();
    const transaction: IDBTransaction = db.transaction([PAYLOADS_STORE, META_STORE], 'readonly');
    const payloads: IDBObjectStore = transaction.objectStore(PAYLOADS_STORE);
    const metaStore: IDBObjectStore = transaction.objectStore(META_STORE);
    // Every request is issued before the first one resolves, so the transaction never goes inactive.
    const payloadRequests: Promise<unknown>[] = ids.map((id: string) => request(payloads.get(id)));
    const metaRequests: Promise<unknown>[] = ids.map((id: string) => request(metaStore.get(id)));
    const [payloadRows, metaRows]: [unknown[], unknown[]] = await Promise.all([
      Promise.all(payloadRequests),
      Promise.all(metaRequests),
    ]);
    const result: Map<string, HsHlodStoredPayload> = new Map();
    ids.forEach((id: string, index: number) => {
      const payload: unknown = payloadRows[index];
      const meta: HsHlodPayloadMeta | null = toMeta(metaRows[index]);
      if (payload instanceof ArrayBuffer && meta !== null) {
        result.set(id, { payload, meta });
      }
    });
    return result;
  }

  public async write(items: readonly HsHlodPayloadStoreItem[]): Promise<void> {
    const db: IDBDatabase = await this.open();
    const transaction: IDBTransaction = db.transaction([PAYLOADS_STORE, META_STORE], 'readwrite');
    const payloads: IDBObjectStore = transaction.objectStore(PAYLOADS_STORE);
    const metaStore: IDBObjectStore = transaction.objectStore(META_STORE);
    items.forEach((item: HsHlodPayloadStoreItem) => {
      payloads.put(item.payload, item.id);
      metaStore.put({ ...item.meta }, item.id);
    });
    await complete(transaction);
  }

  public async touch(ids: readonly string[], touchedAt: number): Promise<void> {
    const db: IDBDatabase = await this.open();
    const transaction: IDBTransaction = db.transaction(META_STORE, 'readwrite');
    const metaStore: IDBObjectStore = transaction.objectStore(META_STORE);
    ids.forEach((id: string) => {
      const getRequest: IDBRequest<unknown> = metaStore.get(id);
      // The put is issued from the success callback, where the transaction is still active.
      getRequest.onsuccess = () => {
        const meta: HsHlodPayloadMeta | null = toMeta(getRequest.result);
        if (meta !== null) {
          metaStore.put({ ...meta, touchedAt }, id);
        }
      };
    });
    await complete(transaction);
  }

  public async entries(): Promise<{ id: string; meta: HsHlodPayloadMeta }[]> {
    const db: IDBDatabase = await this.open();
    const cursorRequest: IDBRequest<IDBCursorWithValue | null> = db
      .transaction(META_STORE, 'readonly')
      .objectStore(META_STORE)
      .index(TOUCHED_INDEX)
      .openCursor();
    const entries: { id: string; meta: HsHlodPayloadMeta }[] = [];
    await new Promise<void>((resolve: () => void, reject: (reason?: unknown) => void) => {
      cursorRequest.onsuccess = () => {
        const cursor: IDBCursorWithValue | null = cursorRequest.result;
        if (cursor === null) {
          resolve();
          return;
        }
        const meta: HsHlodPayloadMeta | null = toMeta(cursor.value);
        if (meta !== null && typeof cursor.primaryKey === 'string') {
          entries.push({ id: cursor.primaryKey, meta });
        }
        cursor.continue();
      };
      cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error('Failed to read cache entries.'));
    });
    return entries;
  }

  public async delete(ids: readonly string[]): Promise<void> {
    const db: IDBDatabase = await this.open();
    const transaction: IDBTransaction = db.transaction([PAYLOADS_STORE, META_STORE], 'readwrite');
    ids.forEach((id: string) => {
      transaction.objectStore(PAYLOADS_STORE).delete(id);
      transaction.objectStore(META_STORE).delete(id);
    });
    await complete(transaction);
  }

  public async clear(): Promise<void> {
    const db: IDBDatabase = await this.open();
    const transaction: IDBTransaction = db.transaction([PAYLOADS_STORE, META_STORE], 'readwrite');
    transaction.objectStore(PAYLOADS_STORE).clear();
    transaction.objectStore(META_STORE).clear();
    await complete(transaction);
  }

  private open(): Promise<IDBDatabase> {
    this.database ??= new Promise<IDBDatabase>(
      (resolve: (db: IDBDatabase) => void, reject: (reason: unknown) => void) => {
        const factory: IDBFactory | undefined = this.options.indexedDb ?? globalThis.indexedDB;
        if (factory === undefined) {
          reject(new Error('IndexedDB is not available.'));
          return;
        }
        const openRequest: IDBOpenDBRequest = factory.open(
          this.options.databaseName ?? 'hyperscene-hlod',
          SCHEMA_VERSION
        );
        openRequest.onupgradeneeded = () => {
          const db: IDBDatabase = openRequest.result;
          if (!db.objectStoreNames.contains(PAYLOADS_STORE)) {
            db.createObjectStore(PAYLOADS_STORE);
          }
          if (!db.objectStoreNames.contains(META_STORE)) {
            db.createObjectStore(META_STORE).createIndex(TOUCHED_INDEX, 'touchedAt');
          }
        };
        openRequest.onsuccess = () => {
          const db: IDBDatabase = openRequest.result;
          // Another tab opened a newer schema: close so its upgrade is not blocked; the next call reopens.
          db.onversionchange = () => {
            db.close();
            this.database = null;
          };
          resolve(db);
        };
        openRequest.onerror = () => reject(openRequest.error ?? new Error('Failed to open IndexedDB.'));
        openRequest.onblocked = () => reject(new Error('IndexedDB upgrade is blocked by another tab.'));
      }
    );
    return this.database;
  }
}

function toMeta(value: unknown): HsHlodPayloadMeta | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const meta: Partial<HsHlodPayloadMeta> = value as Partial<HsHlodPayloadMeta>;
  if (typeof meta.bytes !== 'number' || typeof meta.touchedAt !== 'number') {
    return null;
  }
  return {
    bytes: meta.bytes,
    touchedAt: meta.touchedAt,
    version: typeof meta.version === 'number' ? meta.version : null,
    validatedAt: typeof meta.validatedAt === 'number' ? meta.validatedAt : 0,
  };
}

function request<T>(idbRequest: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve: (value: T) => void, reject: (reason: unknown) => void) => {
    idbRequest.onsuccess = () => resolve(idbRequest.result);
    idbRequest.onerror = () => reject(idbRequest.error ?? new Error('IndexedDB request failed.'));
  });
}

function complete(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve: () => void, reject: (reason: unknown) => void) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted.'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed.'));
  });
}
